'use client'

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { useEffect, useState } from 'react'
import { Bar, BarChart, Label, LabelList, Pie, PieChart, XAxis } from 'recharts'
import RoundDisplay from '../components/roundDisplay'
import RoundSelector from '../components/roundSelector'

export default function PointDistributrionChart({
  round,
  seasonId,
  overallData,
}) {
  const [localRound, setLocalRound] = useState(round)
  const [chartData, setChartData] = useState()

  useEffect(() => {
    function formatData(data) {
      const result = {}

      data?.forEach((d) => {
        if (result[d.round_name]) {
          result[d.round_name].safety =
            result[d.round_name].safety + Math.round(d.safety_points)
          result[d.round_name].pace =
            result[d.round_name].pace + Math.round(d.pace_points)
          result[d.round_name].position =
            result[d.round_name].position + Math.round(d.position_points)
        } else {
          result[d.round_name] = {
            safety: Math.round(d.safety_points),
            pace: Math.round(d.pace_points),
            position: Math.round(d.position_points),
          }
        }
      })
      let cd

      cd = Object.entries(result).map(([key, value]) => {
        return {
          round: key,
          safety: value.safety,
          pace: value.pace,
          position: value.position,
        }
      })

      if (localRound !== 'All Rounds') {
        let result = []
        result[0] = { type: 'safety', value: cd[0].safety, fill: 'white' }
        result[1] = { type: 'pace', value: cd[0].pace, fill: '#FFB88C' }
        result[2] = { type: 'position', value: cd[0].position, fill: '#FF6100' }
        cd = result
        console.log(cd)
      }

      setChartData(cd)
    }
    let data = overallData
    if (localRound !== 'All Rounds') {
      data = data.filter((d) => d.event_id === localRound.event_id)
    }
    formatData(data)
  }, [localRound, overallData])

  useEffect(() => {
    setLocalRound(round)
  }, [round])

  const chartConfig =
    localRound !== 'All Rounds'
      ? {
          value: {
            label: 'value',
          },
          safety: {
            label: 'Safety',
            color: 'hsl(var(--chart-1))',
          },
          pace: {
            label: 'Pace',
            color: 'hsl(var(--chart-2))',
          },
          position: {
            label: 'Position',
            color: 'hsl(var(--chart-3))',
          },
        }
      : {
          valid: {
            label: 'Valid Laps',
            color: '#FF6100',
          },
          invalid: {
            label: 'Invalid Laps',
            color: '#FF6100',
          },
        }

  return (
    <div className="flex h-full flex-col items-center justify-between gap-4">
      <div className="w-full">
        <div className="flex items-center justify-between">
          <h2 className="text-p-2 font-semibold tablet:text-h-5">
            Point Distribution
          </h2>
          <RoundSelector round={localRound} setRound={setLocalRound} />
        </div>
        <p className="mb-5 w-full text-left text-p-1 text-neutral-blue-500">
          Global point distribution across pace, safety and position
        </p>
        <div className="divide bg-neutral-blue-700"></div>
      </div>

      {chartData && (
        <>
          {localRound === 'All Rounds' ? (
            <ChartContainer
              config={chartConfig}
              className="h-full max-h-full min-h-[100px] w-full max-tablet:aspect-[1.3]"
            >
              <BarChart accessibilityLayer data={chartData}>
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6100" />
                    <stop offset="50%" stopColor="#FF4F00" />
                    <stop offset="100%" stopColor="#FF3D00" />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="round"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value, index) => `R${index + 1}`}
                  style={{ fill: '#627C9A' }}
                />
                <ChartTooltip
                  cursor={false}
                  content={() => <ChartTooltipContent hideLabel />}
                />

                <Bar
                  dataKey="safety"
                  stackId={'a'}
                  fill="#FFFFFF"
                  radius={[0, 0, 4, 4]}
                ></Bar>
                <Bar dataKey="pace" stackId={'a'} fill="#FFB88C"></Bar>
                <Bar
                  dataKey="position"
                  stackId={'a'}
                  fill="url(#gradient)"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    position="top"
                    offset={4}
                    className="fill-shades-0"
                    fontSize={10}
                  />
                </Bar>
                <ChartLegend
                  content={() => (
                    <div className="mt-5 flex items-center justify-center gap-6">
                      <div className="flex items-center justify-center gap-2">
                        <section className="size-2 rounded-[2px] bg-shades-0"></section>
                        <p className="text-p-1 text-neutral-blue-500">Safety</p>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <section className="size-2 rounded-[2px] bg-[#FFB88C]"></section>
                        <p className="text-p-1 text-neutral-blue-500">Pace</p>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <section className="size-2 rounded-[2px] bg-primary"></section>
                        <p className="text-p-1 text-neutral-blue-500">
                          Position
                        </p>
                      </div>
                    </div>
                  )}
                />
              </BarChart>
            </ChartContainer>
          ) : (
            <>
              <ChartContainer
                config={chartConfig}
                className="mx-auto aspect-square max-h-[250px]"
              >
                <PieChart>
                  <ChartTooltip
                    cursor={false}
                    content={() => <ChartTooltipContent hideLabel />}
                  />
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="type"
                    innerRadius={60}
                    strokeWidth={5}
                  >
                    <Label
                      content={({ viewBox }) => {
                        if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                          return (
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              <tspan
                                x={viewBox.cx}
                                y={viewBox.cy}
                                className="fill-shades-0 text-3xl font-bold"
                              >
                                {chartData?.[0].value +
                                  chartData?.[1].value +
                                  chartData?.[2].value}
                              </tspan>
                              <tspan
                                x={viewBox.cx}
                                y={(viewBox.cy || 0) + 24}
                                className="fill-neutral-blue-500"
                              >
                                Points
                              </tspan>
                            </text>
                          )
                        }
                      }}
                    />
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="mt-5 flex items-center justify-center gap-6">
                    <div className="flex items-center justify-center gap-2">
                      <section className="size-2 rounded-[2px] bg-shades-0"></section>
                      <p className="text-p-1 text-neutral-blue-500">Safety</p>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <section className="size-2 rounded-[2px] bg-[#FFB88C]"></section>
                      <p className="text-p-1 text-neutral-blue-500">Pace</p>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <section className="size-2 rounded-[2px] bg-primary"></section>
                      <p className="text-p-1 text-neutral-blue-500">Position</p>
                    </div>
                  </div>
                </div>
              </div>
              <RoundDisplay round={localRound}></RoundDisplay>
            </>
          )}
        </>
      )}
    </div>
  )
}
